import { useState } from 'react';
import { Link } from 'wouter';
import { Bot, ChevronRight } from 'lucide-react';
import { useAdminAgentDecisions, useAdminAgentRuns } from '../../api/useAdminOps';
import RunDetailDrawer from './RunDetailDrawer';
import { RUN_STATUS_STYLES, StatusChip, TASK_LABELS, timeAgo } from './opsUi';

// Compact automation banner for the matching page: latest agent runs +
// pending decisions for this event, linking into the ops surfaces. Renders
// nothing when the event has no automation activity.

export default function OpsBanner({ eventTimeId }: { eventTimeId: string }) {
  const { data: runsData } = useAdminAgentRuns({ eventTimeId });
  const { data: decisionsData } = useAdminAgentDecisions({ eventTimeId, status: 'pending' });
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const runs = runsData?.pages[0]?.data?.runs ?? [];
  const pending = decisionsData?.pages[0]?.data?.decisions ?? [];
  if (runs.length === 0 && pending.length === 0) return null;

  const latestRuns = runs.slice(0, 3);

  return (
    <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-3 mb-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Bot className="w-4 h-4 text-primary-500 shrink-0" />
          <span className="text-sm font-semibold">AI event ops</span>
          {pending.length > 0 && (
            <Link
              href="/ops/decisions"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-xs font-medium hover:opacity-80"
            >
              {pending.length} decision{pending.length > 1 ? 's' : ''} need{pending.length === 1 ? 's' : ''} you
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {latestRuns.map((run) => (
            <button
              key={run.id}
              onClick={() => setOpenRunId(run.id)}
              className="inline-flex items-center gap-1.5 text-xs text-dark-muted hover:text-primary-500"
            >
              <StatusChip status={run.status} styles={RUN_STATUS_STYLES} />
              <span>
                {TASK_LABELS[run.taskType] ?? run.taskType}
                {run.neighborhoodName ? ` · ${run.neighborhoodName}` : ''} · {timeAgo(run.createdAt)}
              </span>
            </button>
          ))}
          <Link
            href="/ops"
            className="inline-flex items-center gap-0.5 text-xs font-medium text-primary-500 hover:underline"
          >
            Event Ops <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <RunDetailDrawer runId={openRunId} onClose={() => setOpenRunId(null)} />
    </div>
  );
}
